import React from "react";
import {
  ReactFlow,
  Background,
  MarkerType,
  Position,
  Handle,
  useNodesState,
  useEdgesState,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  FiUser,
  FiActivity,
  FiFileText,
  FiHexagon,
  FiLink,
} from "react-icons/fi";
import { FaUserInjured, FaUserMd } from "react-icons/fa";
import "./EsmBlockchain.scss";
import { useTranslation } from "react-i18next";

const PatientNode = ({ data }) => (
  <div className="eb-node-actor" style={{ position: "relative" }}>
    <Handle
      type="target"
      position={Position.Left}
      id="left"
      style={{ opacity: 0 }}
    />
    <div className="eb-icon">
      <FaUserInjured />
    </div>
    <div className="eb-text">{data.label}</div>
    <Handle
      type="source"
      position={Position.Right}
      id="right"
      style={{ opacity: 0 }}
    />
  </div>
);

const ExaminationNode = ({ data }) => (
  <div className="eb-node-actor" style={{ position: "relative" }}>
    <Handle
      type="target"
      position={Position.Left}
      id="left"
      style={{ opacity: 0 }}
    />
    <div className="eb-icon">
      <FaUserMd />
    </div>
    <div className="eb-text">{data.label}</div>
    <Handle
      type="source"
      position={Position.Right}
      id="right"
      style={{ opacity: 0 }}
    />
  </div>
);

const PaymentNode = ({ data }) => (
  <div className="eb-node-payment" style={{ position: "relative" }}>
    <Handle
      type="target"
      position={Position.Left}
      id="left"
      style={{ opacity: 0 }}
    />
    <div className="eb-text" style={{ whiteSpace: "nowrap" }}>
      {data.label.split("\n").map((l, i) => (
        <div key={i}>{l}</div>
      ))}
    </div>
    <Handle
      type="source"
      position={Position.Right}
      id="right"
      style={{ opacity: 0 }}
    />
  </div>
);

const BlockchainNode = ({ data }) => (
  <div className="eb-node-blockchain" style={{ position: "relative" }}>
    <Handle
      type="target"
      position={Position.Left}
      id="left"
      style={{ opacity: 0 }}
    />
    <FiHexagon className="hex-icon" />
    <div className="core"></div>
    {data.showLabelLeft && (
      <div className="side-label left">{data.labelLeft}</div>
    )}
    {data.showLabelRight && (
      <div className="side-label right">{data.labelRight}</div>
    )}
    <Handle
      type="source"
      position={Position.Right}
      id="right"
      style={{ opacity: 0 }}
    />
  </div>
);

const MedicalRecordNode = ({ data }) => (
  <div className="eb-node-record" style={{ position: "relative" }}>
    <Handle
      type="target"
      position={Position.Left}
      id="left"
      style={{ opacity: 0 }}
    />
    <FiFileText className="doc-icon" />
    <div className="badge">
      <FiLink />
    </div>
    <Handle
      type="source"
      position={Position.Right}
      id="right"
      style={{ opacity: 0 }}
    />
  </div>
);

const ArchitectureNode = ({ data }) => (
  <div className="eb-node-architecture" style={{ position: "relative" }}>
    <Handle
      type="target"
      position={Position.Top}
      id="top"
      style={{ opacity: 0 }}
    />
    <Handle
      type="target"
      position={Position.Bottom}
      id="bottom"
      style={{ opacity: 0 }}
    />
    <Handle
      type="source"
      position={Position.Bottom}
      id="src-bottom"
      style={{ opacity: 0 }}
    />
    <Handle
      type="target"
      position={Position.Left}
      id="left"
      style={{ opacity: 0 }}
    />
    <div className="eb-text">{data.label}</div>
  </div>
);

const SimpleLabelNode = ({ data }) => (
  <div
    className="eb-node-simple-label"
    style={{ ...data.style, position: "relative" }}
  >
    {data.label.split("\n").map((l, i) => (
      <div key={i}>{l}</div>
    ))}
    <Handle
      type="source"
      position={Position.Bottom}
      id="bottom"
      style={{ opacity: 0 }}
    />
    <Handle
      type="source"
      position={Position.Right}
      id="right"
      style={{ opacity: 0 }}
    />
  </div>
);

const InvisibleNode = ({ data }) => (
  <div style={{ width: 1, height: 1, position: "relative" }}>
    <Handle
      type="target"
      position={Position.Left}
      id="left"
      style={{ opacity: 0 }}
    />
    <Handle
      type="target"
      position={Position.Top}
      id="top"
      style={{ opacity: 0 }}
    />
    <Handle
      type="source"
      position={Position.Bottom}
      id="bottom"
      style={{ opacity: 0 }}
    />
    <Handle
      type="source"
      position={Position.Right}
      id="right"
      style={{ opacity: 0 }}
    />
  </div>
);

const nodeTypes = {
  patientNode: PatientNode,
  examinationNode: ExaminationNode,
  paymentNode: PaymentNode,
  blockchainNode: BlockchainNode,
  medicalRecordNode: MedicalRecordNode,
  architectureNode: ArchitectureNode,
  simpleLabel: SimpleLabelNode,
  invisible: InvisibleNode,
};

const EsmBlockchain = () => {
  const { t } = useTranslation();

  const initialNodes = [
    // Labels
    {
      id: "lbl-giaodich",
      type: "simpleLabel",
      position: { x: 50, y: 30 },
      data: {
        label: t("esm_blockchain_diagram.transaction"),
        style: { color: "#002B5B", fontWeight: 700, fontSize: 28 },
      },
    },
    {
      id: "lbl-dulieu",
      type: "simpleLabel",
      position: { x: 200, y: 195 },
      data: {
        label: t("esm_blockchain_diagram.data_decentralized"),
        style: {
          color: "#0078C8",
          fontWeight: 700,
          fontSize: 18,
          textAlign: "center",
        },
      },
    },
    {
      id: "lbl-hoso",
      type: "simpleLabel",
      position: { x: 180, y: 305 },
      data: {
        label: t("esm_blockchain_diagram.smart_records"),
        style: {
          color: "#0078C8",
          fontWeight: 700,
          fontSize: 18,
          textAlign: "center",
        },
      },
    },

    // Top Flow
    {
      id: "patient",
      type: "patientNode",
      position: { x: 200, y: 40 },
      data: { label: t("esm_blockchain_diagram.patient") },
    },
    {
      id: "exam",
      type: "examinationNode",
      position: { x: 550, y: 40 },
      data: { label: t("esm_blockchain_diagram.exam") },
    },
    {
      id: "payment",
      type: "paymentNode",
      position: { x: 950, y: 65 },
      data: { label: t("esm_blockchain_diagram.payment_meds") },
    },

    // Blockchain Chain (7 nodes)
    {
      id: "bc-1",
      type: "blockchainNode",
      position: { x: 300, y: 180 },
      data: {},
    },
    {
      id: "bc-2",
      type: "blockchainNode",
      position: { x: 420, y: 180 },
      data: {},
    },
    {
      id: "bc-3",
      type: "blockchainNode",
      position: { x: 540, y: 180 },
      data: {},
    },
    {
      id: "bc-4",
      type: "blockchainNode",
      position: { x: 660, y: 180 },
      data: {},
    },
    {
      id: "bc-5",
      type: "blockchainNode",
      position: { x: 780, y: 180 },
      data: {},
    },
    {
      id: "bc-6",
      type: "blockchainNode",
      position: { x: 900, y: 180 },
      data: {},
    },
    {
      id: "bc-7",
      type: "blockchainNode",
      position: { x: 1020, y: 180 },
      data: {},
    },

    // Medical Records (7 nodes)
    {
      id: "mr-1",
      type: "medicalRecordNode",
      position: { x: 310, y: 300 },
      data: {},
    },
    {
      id: "mr-2",
      type: "medicalRecordNode",
      position: { x: 430, y: 300 },
      data: {},
    },
    {
      id: "mr-3",
      type: "medicalRecordNode",
      position: { x: 550, y: 300 },
      data: {},
    },
    {
      id: "mr-4",
      type: "medicalRecordNode",
      position: { x: 670, y: 300 },
      data: {},
    },
    {
      id: "mr-5",
      type: "medicalRecordNode",
      position: { x: 790, y: 300 },
      data: {},
    },
    {
      id: "mr-6",
      type: "medicalRecordNode",
      position: { x: 910, y: 300 },
      data: {},
    },
    {
      id: "mr-7",
      type: "medicalRecordNode",
      position: { x: 1030, y: 300 },
      data: {},
    },

    // Architecture Nodes
    {
      id: "arch-home",
      type: "architectureNode",
      position: { x: 95, y: 420 },
      data: { label: t("esm_blockchain_diagram.home_token") },
    },
    {
      id: "arch-master",
      type: "architectureNode",
      position: { x: 345, y: 420 },
      data: { label: t("esm_blockchain_diagram.master_node") },
    },
    {
      id: "arch-pow",
      type: "architectureNode",
      position: { x: 600, y: 420 },
      data: { label: t("esm_blockchain_diagram.pow_pos") },
    },
    {
      id: "arch-consensus",
      type: "architectureNode",
      position: { x: 855, y: 420 },
      data: { label: t("esm_blockchain_diagram.consensus") },
    },

    // Invisible end nodes to create the long horizontal runs to the right
    {
      id: "end-consensus",
      type: "invisible",
      position: { x: 1100, y: 480 },
      data: {},
    },
    {
      id: "end-pow",
      type: "invisible",
      position: { x: 1100, y: 500 },
      data: {},
    },
    {
      id: "end-master",
      type: "invisible",
      position: { x: 1100, y: 520 },
      data: {},
    },
    {
      id: "end-home",
      type: "invisible",
      position: { x: 1100, y: 540 },
      data: {},
    },
    {
      id: "end-giao",
      type: "invisible",
      position: { x: 1100, y: 560 },
      data: {},
    },
  ];

  const mainEdgeStyle = { stroke: "#0078C8", strokeWidth: 3 };
  const bcEdgeStyle = {
    stroke: "#4a5568",
    strokeWidth: 2,
    strokeDasharray: "5 5",
  };

  const initialEdges = [
    // Top Flow - Straight edges
    {
      id: "e-p-e",
      source: "patient",
      sourceHandle: "right",
      target: "exam",
      targetHandle: "left",
      type: "straight",
      style: mainEdgeStyle,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: "#0078C8",
        width: 15,
        height: 15,
      },
    },
    {
      id: "e-e-p",
      source: "exam",
      sourceHandle: "right",
      target: "payment",
      targetHandle: "left",
      type: "straight",
      style: mainEdgeStyle,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: "#0078C8",
        width: 15,
        height: 15,
      },
    },

    // Giao dich line
    {
      id: "e-gd-patient",
      source: "lbl-giaodich",
      sourceHandle: "bottom",
      target: "patient",
      targetHandle: "left",
      type: "step",
      style: mainEdgeStyle,
      markerEnd: {
        type: MarkerType.ArrowClosed,
        color: "#0078C8",
        width: 15,
        height: 15,
      },
    },

    // Independent L-shaped architecture layers
    {
      id: "e-gd-bus",
      source: "lbl-giaodich",
      sourceHandle: "bottom",
      target: "end-giao",
      targetHandle: "left",
      type: "step",
      style: mainEdgeStyle,
    },
    {
      id: "e-arch-home",
      source: "arch-home",
      sourceHandle: "src-bottom",
      target: "end-home",
      targetHandle: "left",
      type: "step",
      style: mainEdgeStyle,
    },
    {
      id: "e-arch-master",
      source: "arch-master",
      sourceHandle: "src-bottom",
      target: "end-master",
      targetHandle: "left",
      type: "step",
      style: mainEdgeStyle,
    },
    {
      id: "e-arch-pow",
      source: "arch-pow",
      sourceHandle: "src-bottom",
      target: "end-pow",
      targetHandle: "left",
      type: "step",
      style: mainEdgeStyle,
    },
    {
      id: "e-arch-consensus",
      source: "arch-consensus",
      sourceHandle: "src-bottom",
      target: "end-consensus",
      targetHandle: "left",
      type: "step",
      style: mainEdgeStyle,
    },

    // Blockchain edges
    {
      id: "e-bc-1",
      source: "bc-1",
      sourceHandle: "right",
      target: "bc-2",
      targetHandle: "left",
      type: "straight",
      style: bcEdgeStyle,
    },
    {
      id: "e-bc-2",
      source: "bc-2",
      sourceHandle: "right",
      target: "bc-3",
      targetHandle: "left",
      type: "straight",
      style: bcEdgeStyle,
    },
    {
      id: "e-bc-3",
      source: "bc-3",
      sourceHandle: "right",
      target: "bc-4",
      targetHandle: "left",
      type: "straight",
      style: bcEdgeStyle,
    },
    {
      id: "e-bc-4",
      source: "bc-4",
      sourceHandle: "right",
      target: "bc-5",
      targetHandle: "left",
      type: "straight",
      style: bcEdgeStyle,
    },
    {
      id: "e-bc-5",
      source: "bc-5",
      sourceHandle: "right",
      target: "bc-6",
      targetHandle: "left",
      type: "straight",
      style: bcEdgeStyle,
    },
    {
      id: "e-bc-6",
      source: "bc-6",
      sourceHandle: "right",
      target: "bc-7",
      targetHandle: "left",
      type: "straight",
      style: bcEdgeStyle,
    },

    // Medical Records chain edges
    {
      id: "e-mr-1",
      source: "mr-1",
      sourceHandle: "right",
      target: "mr-2",
      targetHandle: "left",
      type: "straight",
      style: bcEdgeStyle,
    },
    {
      id: "e-mr-2",
      source: "mr-2",
      sourceHandle: "right",
      target: "mr-3",
      targetHandle: "left",
      type: "straight",
      style: bcEdgeStyle,
    },
    {
      id: "e-mr-3",
      source: "mr-3",
      sourceHandle: "right",
      target: "mr-4",
      targetHandle: "left",
      type: "straight",
      style: bcEdgeStyle,
    },
    {
      id: "e-mr-4",
      source: "mr-4",
      sourceHandle: "right",
      target: "mr-5",
      targetHandle: "left",
      type: "straight",
      style: bcEdgeStyle,
    },
    {
      id: "e-mr-5",
      source: "mr-5",
      sourceHandle: "right",
      target: "mr-6",
      targetHandle: "left",
      type: "straight",
      style: bcEdgeStyle,
    },
    {
      id: "e-mr-6",
      source: "mr-6",
      sourceHandle: "right",
      target: "mr-7",
      targetHandle: "left",
      type: "straight",
      style: bcEdgeStyle,
    },
  ];

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  return (
    <div className="esm-blockchain-diagram-wrapper">
      <div className="eb-title">
        {t("esm_blockchain_diagram.title", "Phát triển nền tảng Block Chain")}
      </div>
      <div className="eb-react-flow-container">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          fitView={true}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          zoomOnScroll={true}
          panOnDrag={true}
          preventScrolling={false}
          minZoom={0.5}
          maxZoom={2}
        ></ReactFlow>
      </div>
    </div>
  );
};

export default EsmBlockchain;
